import { api } from '@/lib/api-client'

// ── /hod/sites ────────────────────────────────────────────────────────────
// Slice G (backend, 2026-05-12). Returns every site under the caller's
// departments_owned with full metadata + per-site issue counts. Drives the
// HOD Site Score leaderboard (real full list, not derived from issues)
// and the /hod/departments redesign (dept → sites grid).
//
// Auth: hod only. Zero owned departments → 200 + sites: [] +
// no_departments: true (backend change 2026-07-17 — see
// docs/FE_HANDOFF_session_expired_403.md in the backend repo). 403 only
// when a passed department_id isn't owned by the caller.

// Lifecycle v2: counts follow the STORED status enum (open | pending |
// completed). Overdue is derived per-issue from resolution_eta and is not
// broken out per site — overdue rows are inside `pending` here.
export interface HodSiteIssueCounts {
  open: number
  pending: number
  completed: number
  /** severity='critical' AND status <> 'completed'. Drives the red badge. */
  critical_active: number
}

/** Per-site survey rollup. Shipped in Slice I (2026-05-13) — drives both
 *  the dashboard "Survey health" widget (summed client-side across all
 *  sites in the response) and the per-site survey badge on the My Sites
 *  page. No separate /hod/surveys endpoint is provided — survey drill-down
 *  stays on /sites/{id}/details. */
export interface HodSiteSurveyCounts {
  pending: number
  completed: number
  missed: number
  total: number
}

export interface HodSiteEntityRef {
  id: string
  name: string
}

export interface HodSiteDepartmentRef {
  id: string
  name: string
}

export interface HodSitePersonRef {
  id: string
  display_name: string
  contact_number: string | null
}

export interface HodSite {
  id: string
  name: string
  address: string | null
  mandal: string | null
  lat: number | null
  long: number | null
  geofence_radius_meters: number | null
  contact_number: string | null
  site_image_url: string | null
  is_active: boolean
  entity: HodSiteEntityRef
  department: HodSiteDepartmentRef
  // Lifecycle v2 removed the Site Representative role — the backend no
  // longer returns a site_rep object. assigned_officer (the site's current
  // in-charge FO) is the on-site contact now.
  assigned_officer: HodSitePersonRef | null
  issue_counts: HodSiteIssueCounts
  survey_counts: HodSiteSurveyCounts
  last_survey_date: string | null
  last_event_at: string | null
}

export interface HodSitesPagination {
  page: number
  page_size: number
  total_count: number
  total_pages: number
  has_next: boolean
  has_previous: boolean
}

export interface HodSitesResponse {
  sites: HodSite[]
  /** Only present (and true) when the caller owns zero active departments —
   *  the onboarding state. Absent on normal responses; treat missing as false. */
  no_departments?: boolean
  pagination: HodSitesPagination
}

export interface HodSitesParams {
  department_id?: string
  /** Default true at the backend; pass false to also return inactive sites. */
  active?: boolean
  /** Default true at the backend; pass false to hide sites with zero issues. */
  include_zero_issue?: boolean
  page?: number
  page_size?: number
}

export function listHodSites(params: HodSitesParams = {}): Promise<HodSitesResponse> {
  return api.get<HodSitesResponse>('/hod/sites', { params })
}
